import type { BacklogTaskResult } from '@ai-meeting/shared'
import { isAlreadyCreated, needsConfirmation } from '../utils/backlogConfirmDraft'
import { ExclamationTriangleIcon, ExternalLinkIcon } from '@/components/Icons'
import type { UiStrings } from '@/constants/ui-locale'
import { splitAssignee } from '@/utils/directoryValue'

/**
 * The two cells that say what the push will do to one action item, and what it
 * has done so far (BRAI-575).
 *
 * They describe one row and are read before anything is pressed; the bar under
 * the table (`BacklogSyncSummaryBar`) counts the batch afterwards.
 */

const PILL = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold'

/**
 * The issue type this item will be filed as on Backlog.
 *
 * A tag while reading and a picker while editing, matching the rest of the
 * row: the value is saved on the action item like `assignee` and `deadline`,
 * so it is minutes content rather than a decision about one push, and it is
 * edited where the rest of the minutes are.
 *
 * Per item although `POST /tasks` takes one issue type per request — the
 * backend groups the items by this name and sends a request per group, so one
 * meeting can file a bug and two tasks. Unset is the ordinary state and files
 * the item under the project's configured default.
 *
 * @param value The saved name, or `''` when the item carries none.
 * @param issueTypes Names this project declares; empty while they are still
 *   being read, or if they could not be, which leaves only the default.
 * @param readOnly The minutes are being read rather than edited.
 * @param rowNumber 1-based position, so the picker's label names its row.
 */
export function BacklogIssueTypeCell({
  value,
  issueTypes,
  onChange,
  readOnly,
  rowNumber,
  t,
}: {
  value: string
  issueTypes: string[]
  onChange: (issueType: string) => void
  readOnly: boolean
  rowNumber: number
  t: UiStrings
}) {
  if (readOnly) {
    // An em dash, not the words "project default": this is a label among other
    // labels, and spelling out a fallback on every unset row would shout over
    // the rows that do say something.
    if (!value) return <span className="text-xs text-[var(--text-tertiary)]">—</span>
    return (
      <span
        className={`${PILL} bg-[var(--accent-light)] text-[var(--accent)]`}
        data-testid={`action-issue-type-tag-${rowNumber - 1}`}
      >
        {value}
      </span>
    )
  }

  // Nothing to choose between: a picker holding only the default would be a
  // control that cannot do anything, which reads as broken rather than empty.
  if (issueTypes.length === 0) {
    return <span className="text-xs text-[var(--text-tertiary)]">—</span>
  }

  return (
    <select
      className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-2 py-1 text-xs text-[var(--text-primary)] focus:border-[var(--accent)] focus:outline-none"
      value={value}
      aria-label={`${t.actionItemsTableIssueType} ${String(rowNumber).padStart(2, '0')}`}
      data-field={`action-issue-type-${rowNumber - 1}`}
      onChange={(event) => onChange(event.target.value)}
    >
      {/* Listed first and always present: clearing a choice has to be as
          reachable as making one, and it is the state every item starts in. */}
      <option value="">{t.backlogIssueTypeDefault}</option>
      {/* A name the minutes already carry but the project no longer declares
          would otherwise vanish from the picker and read as never set. */}
      {(issueTypes.includes(value) || !value ? issueTypes : [value, ...issueTypes]).map(
        (issueType) => (
          <option key={issueType} value={issueType}>
            {issueType}
          </option>
        )
      )}
    </select>
  )
}

/**
 * The Backlog cell: what the push will do to this row, or what it did.
 *
 * Reads as a label, not a control. The reviewer steers the push with the row's
 * tick box and, for a matched issue, with the confirmation panel below it;
 * which of create-or-update happens is the server's match result, and offering
 * it as a choice here would be offering to overrule a decision this screen
 * cannot recompute.
 *
 * @param row The verdict for this action item, absent while the check runs.
 * @param excluded The reviewer unticked this row, so nothing will be sent.
 */
export function BacklogActionCell({
  row,
  excluded,
  checking,
  t,
}: {
  row: BacklogTaskResult | undefined
  excluded: boolean
  checking: boolean
  t: UiStrings
}) {
  // Checked first: an unticked row is going nowhere, so the verdict underneath
  // it describes a push that will not happen.
  if (excluded) {
    return <span className="text-xs text-[var(--text-tertiary)]">{t.backlogRowExcluded}</span>
  }

  if (checking) {
    return <span className="text-xs text-[var(--text-tertiary)]">{t.backlogChecking}</span>
  }

  // A row the server refused — an empty action item, most often — is not going
  // to create anything, so naming an action would promise a write that will
  // not happen.
  if (!row || row.status === 'failed') {
    return <span className="text-xs text-[var(--text-tertiary)]">—</span>
  }

  const ticket = row.issue_key ?? row.matched_issue_key
  // A duplicate is done, like a created or updated row: the ticket exists, and
  // this cell is a label for what the push will do, not what it did last time.
  const done =
    row.status === 'created' || row.status === 'updated' || isAlreadyCreated(row)
  const label =
    row.status === 'created'
      ? t.backlogActionCreated
      : row.status === 'updated'
        ? t.backlogRowUpdated
        : // Before the create/update fork, because a duplicate reaches here
          // with `action_type: 'skipped_duplicate'` and would otherwise fall
          // through to "Create new" — promising a ticket the server has
          // already refused to make a second time.
          isAlreadyCreated(row)
          ? t.backlogRowDuplicate
          : row.action_type === 'update' && ticket
            ? t.backlogActionUpdate
            : t.backlogActionCreate

  return (
    <span className="flex flex-wrap items-baseline gap-x-1 text-xs">
      <span className={done ? 'text-[var(--text-secondary)]' : 'font-semibold text-[var(--accent)]'}>
        {label}
      </span>
      {ticket && <span className="text-[var(--text-tertiary)]">·</span>}
      {/* The key is a link rather than part of the label: the one thing a
          reviewer wants from this cell is the ticket it is talking about. */}
      {ticket && row.url && (
        <a
          href={row.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 whitespace-nowrap font-semibold text-[var(--accent)] hover:underline"
        >
          {ticket}
          <ExternalLinkIcon className="h-3 w-3" />
        </a>
      )}
      {ticket && !row.url && (
        <span className="whitespace-nowrap font-semibold text-[var(--text-secondary)]">
          {ticket}
        </span>
      )}
    </span>
  )
}

/**
 * The Status cell: how far this row has got, which is not the ticket's own
 * status on Backlog.
 *
 * Before BRAI-575 this column showed `current_status` — the Backlog workflow
 * state of a matched ticket. Useful, but it answered a question nobody was
 * asking at this moment: the reviewer is about to press Push and wants to know
 * what has and has not gone out.
 *
 * @param row The verdict for this action item; `null`/`dry_run` until a real push.
 * @param excluded The reviewer unticked this row.
 */
export function BacklogRowStatusCell({
  row,
  excluded,
  confirmRequired,
  t,
}: {
  row: BacklogTaskResult | undefined
  excluded: boolean
  /**
   * Whether this matched overwrite still has anything to confirm. False for a
   * row whose values the ticket already carries — it reads as settled rather
   * than as a question nobody can answer (BRAI-806).
   */
  confirmRequired: boolean
  t: UiStrings
}) {
  if (excluded) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-dashed border-[var(--border-color)] px-2 py-0.5 text-[11px] font-semibold text-[var(--text-tertiary)]">
        — {t.backlogRowSkipped}
      </span>
    )
  }

  if (row?.status === 'failed') {
    return (
      <span className={`${PILL} bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300`}>
        {t.backlogRowFailed}
      </span>
    )
  }

  // Settled, but not by this push. Marked with a tick because there is nothing
  // left to do on this row, and left in the neutral pill rather than the green
  // one because green on this table means "this press wrote it".
  if (isAlreadyCreated(row)) {
    return (
      <span className={`${PILL} bg-[var(--bg-tertiary)] text-[var(--text-secondary)]`}>
        ✓ {t.backlogRowDuplicate}
      </span>
    )
  }

  // Every overwrite waits here, not just the ones the server stopped on: a
  // confident match would otherwise have been written by the batch, and
  // Backlog has no delete API to undo one with.
  if (needsConfirmation(row)) {
    // Still a matched overwrite either way. Whether it is a question depends
    // on there being an edit in it: confirming a row the ticket already
    // matches would write nothing, and "Not pushed" below would be the wrong
    // thing to say about a ticket that is in step with the minutes.
    return confirmRequired ? (
      <span
        className={`${PILL} border border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300`}
      >
        <ExclamationTriangleIcon className="h-3 w-3" />
        {t.backlogRowNeedsConfirm}
      </span>
    ) : (
      <span className={`${PILL} bg-[var(--bg-tertiary)] text-[var(--text-secondary)]`}>
        ✓ {t.backlogRowUpToDate}
      </span>
    )
  }

  if (row?.status === 'created' || row?.status === 'updated') {
    return (
      <span
        className={`${PILL} bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300`}
      >
        ✓ {row.status === 'created' ? t.backlogActionCreated : t.backlogRowUpdated}
      </span>
    )
  }

  return (
    <span className={`${PILL} bg-[var(--bg-tertiary)] text-[var(--text-secondary)]`}>
      ○ {t.backlogRowNotPushed}
    </span>
  )
}

/**
 * The assignee as the pushed ticket will carry them: who, over what identifies
 * them.
 *
 * @param value The stored assignee, in whichever format wrote it.
 * @param placeholder Shown when the action item names nobody.
 */
export function AssigneeIdentity({
  value,
  placeholder,
}: {
  value: string
  placeholder: string
}) {
  if (!value.trim()) {
    return <span className="text-sm text-[var(--text-tertiary)]">{placeholder}</span>
  }

  const { name, detail } = splitAssignee(value)

  return (
    <span className="flex items-start gap-2">
      <span
        aria-hidden
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--accent-light)] text-[11px] font-semibold uppercase text-[var(--accent)]"
      >
        {name.charAt(0)}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm text-[var(--text-primary)]">{name}</span>
        {detail && (
          <span className="block truncate text-[11px] text-[var(--text-tertiary)]">{detail}</span>
        )}
      </span>
    </span>
  )
}
